import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState, useCallback } from "react";
import { TableData } from "@/App/Distributions/Pending/Table";
import { useAuth } from "@/hooks/auth";
import { getAllDistributionsProcessByUnityId } from "@/Api/distributionsProcess";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";

type ContestType = {
  id: number;
  name: string;
  validation_date: string;
  prize: number;
  is_active: boolean | number;
  contest_product_balance: {
    id: number;
    uuid: string;
    contest_id: number;
    cp_classification_id: number;
    balance: number;
    with_held_balance: number;
  };
};

type ProductClassificationType = {
  id: number;
  name: string;
  quota_quantity: number;
};

type LotteryType = {
  id: number;
  name: string;
  color?: string;
};

type UnityType = {
  id: number;
  name: string;
};

type CreatedByType = {
  id: number;
  name: string;
};

type SellerType = {
  id: number;
  uuid: string;
  user_id: number;
  user: {
    id: number;
    name: string;
  };
};

type DistributionType = {
  id: number;
  uuid: string;
  cp_classification_id: number;
  unity_id: number;
  quota_quantity: number;
  distribution_type_id: number;
  distribution_process_status_id: number;
  updated_by: CreatedByType;
  created_at: string;
  created_by: CreatedByType;
  updated_at: string;
  processed_at: string | null;
  from_unity_id: number | null;
  from_seller_id: number | null;
  from_unity: UnityType | null;
  from_seller: SellerType | null;
  contest_product_classification: {
    id: number;
    product_name: string;
    product_classification_id: number;
    contest_id: number;
    lottery_id: number;
    product_classification: ProductClassificationType;
    contest: ContestType | null;
    lottery: LotteryType;
  };
  unity: UnityType;
  distribution_type: {
    id: number;
    name: string;
  };
  distribution_process_status: {
    id: number;
    name: string;
  };
};

type GroupedDistributionsType = {
  contests: {
    id: string;
    name: string | null;
    lottery: string;
    products: {
      id: number;
      name: string;
      classification: string;
      quotas: number;
      quota_quantity_utilized: number;
      distribution_process_uuid?: string;
      distribution_process_status_id: number;
      distribution_process_status_name: string;
      distribution_type_id?: number;
      created_by: CreatedByType;
      updated_by: CreatedByType;
      updated_at: string;
      created_at?: string;
      from_unity_name?: string;
      from_seller_name?: string;
      origin_type?: "unity" | "seller";
    }[];
  }[];
};

type StatusFilter = "all" | "pending" | "accepted" | "rejected";

export const PageList = () => {
  const [state, setState] = useState<"loading" | "production" | "error">(
    "loading"
  );
  const [allData, setAllData] = useState<DistributionType[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedDistributionsType>({
    contests: [],
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const { hasPermission } = usePermissions();
  const unityId = useAuth()?.user?.unity?.[0]?.id;

  const filterDataByStatus = useCallback((data: DistributionType[], filter: StatusFilter) => {
    let filtered = data;

    if (filter === "pending") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.PENDING);
    } else if (filter === "accepted") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED);
    } else if (filter === "rejected") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.REJECTED);
    }

    const contestsMap = new Map();

    filtered.forEach((item: DistributionType) => {
      if (!item.contest_product_classification.contest) {
        return;
      }

      const isActive = item.contest_product_classification.contest.is_active;
      if (isActive === false || isActive === 0) {
        return;
      }

      if (![1, 3, 5].includes(item?.distribution_type_id)) {
        return;
      }

      const contestId =
        item.contest_product_classification.contest_id.toString();

      if (!contestsMap.has(contestId)) {
        contestsMap.set(contestId, {
          id: contestId,
          name: item.contest_product_classification.contest?.name ?? null,
          lottery: item.contest_product_classification.lottery.name,
          lottery_color: item.contest_product_classification?.lottery?.color || "transparent",
          lottery_id: item.contest_product_classification.lottery_id,
          products: [],
        });
      }

      const contest = contestsMap.get(contestId);

      contest.products.push({
        id: item.id,
        name: item.contest_product_classification.product_name,
        classification:
          item.contest_product_classification.product_classification.name,
        quotas: item.quota_quantity,
        quota_quantity_utilized: 0,
        distribution_process_uuid: item.uuid,
        distribution_process_status_id: item.distribution_process_status_id,
        distribution_process_status_name:
          item.distribution_process_status.name,
        distribution_type_id: item.distribution_type_id,
        product_classification:
          item.contest_product_classification.product_classification,
        created_by: item.created_by,
        created_at: item.created_at,
        updated_by: item.updated_by,
        updated_at: item?.processed_at || item.updated_at,
        from_unity_name: item.from_unity?.name,
        from_seller_name: item.from_seller?.user?.name,
        origin_type: item.from_seller_id ? "seller" : item.from_unity_id ? "unity" : undefined,
      });
    });

    setGroupedData({
      contests: Array.from(contestsMap.values()),
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!unityId) return;

    try {
      setState("loading");
      const { data } = await getAllDistributionsProcessByUnityId(unityId.toString());

      const distributions = data?.response?.data || [];
      setAllData(distributions);

      setState("production");
    } catch (error) {
      console.error(error);
      setState("error");
    }
  }, [unityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterDataByStatus(allData, statusFilter);
  }, [statusFilter, allData, filterDataByStatus]);

  const pageActions = {
    update: () => {
      fetchData();
    },
  };

  const getStatusCounts = () => {
    const validData = allData.filter(item => {
      if (!item.contest_product_classification.contest) return false;
      const isActive = item.contest_product_classification.contest.is_active;
      if (isActive === false || isActive === 0) return false;
      if (![1, 3, 5].includes(item?.distribution_type_id)) return false;
      return true;
    });

    const pending = validData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.PENDING).length;
    const accepted = validData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED).length;
    const rejected = validData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.REJECTED).length;
    return { pending, accepted, rejected, all: validData.length };
  };

  const counts = getStatusCounts();

  return (
    <Main>
      <If condition={hasPermission("view-distributions-list")}>
        <div className="flex flex-col gap-4">
          <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
            <h2 className="text-2xl font-semibold flex gap-2 items-center">
              Distribuições Recebidas
            </h2>
          </div>

          <Tabs value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)} className="w-full">
            <TabsList className="grid w-full grid-cols-4 max-w-xl">
              <TabsTrigger value="all" className="flex gap-2">
                Todas
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {counts.all}
                </span>
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex gap-2">
                Pendentes
                {counts.pending > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="accepted" className="flex gap-2">
                Aceitas
                {counts.accepted > 0 && (
                  <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.accepted}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected" className="flex gap-2">
                Rejeitadas
                {counts.rejected > 0 && (
                  <span className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.rejected}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-col gap-3">
            {state === "loading" ? (
              <CreateLoaderTable cols={4} lines={6} />
            ) : (
              <TableData
                groupedData={groupedData}
                pageActions={pageActions}
              />
            )}
          </div>
        </div>
      </If>
    </Main>
  );
};
